import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    generateRandomNumber,
    generateRandomString,
    getIframeSrcOrigin,
    getProject,
    getSession,
    getToken,
    getUserAuthData,
    removeSession,
    setSession,
    setTempUserId,
} from '../../../common/tools';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    HEADER_LOGO,
    PROFILE_ID_LOGO,
    PROFILE_LOGOUT_LOGO,
    PROFILE_SETTINGS_LOGO,
    PROFILE_TRANSACTIONS_LOGO,
    WEB_SOURCE,
    PROFILE_LOGO,
    GAME_IFRAME_ID,
    IFRAME_ORIGINS,
    STATIC_FILES_PATH,
    PROFILE_SUPPORT_LOGO,
} from '../../../common/constants';
import i18n from 'i18next';
import {
    AUTH_TYPE,
    ILanguages,
    ISessionCreateData,
    ISingleGame,
    IWebAuthData,
    SessionCreateDataWithIframe,
} from '../../../interfaces';
import { LANGUAGES } from '../../../i18n';
import { sessionGet } from '../../../stores/auth.store';
import { toast } from 'react-toastify';
import { AppleSvg, FacebookSvg, GoogleSvg, MailSvg, TelegramSvg, VkSvg, YandexSvg } from '../../../icons';
import { MainContext } from '../../../context/main.context';
import AuthMain from '../../auth';
import Payments from '../../payments';
import Transactions from '../../transactions';
import SupportTickets from '../../support/tickets';

type PLAYHUB_TO_IFRAME =
    'PLAYHUB_TO_IFRAME_USER_AUTH' |
    'PLAYHUB_TO_IFRAME_USER_LOGOUT' |
    'PLAYHUB_TO_IFRAME_PAYMENT_RESULT' |
    'PLAYHUB_TO_IFRAME_CHANGE_LNG' |
    'PLAYHUB_TO_IFRAME_UNREAD_SUPPORT_MSG';

interface Props {
    isGame: boolean
    projectData?: ISingleGame
    project?: string
}


export default function HeaderGames({ isGame, projectData }: Props) {
    const { t } = useTranslation();
    const { language, changeLanguage } = i18n;
    const navigate = useNavigate();
    const {
        settings,
        userAuthData,
        toggleLoader,
        setModalContent,
        setUserAuth,
        supportUnreadMessageCount,
        getSupportUnreadMessageCountPublic,
    } = useContext(MainContext);

    const [ isHovered, setIsHovered ] = useState<boolean>(false);
    const [ isLngModalOpen, setIsLngModalOpen ] = useState<boolean>(false);
    const [ isProfileModalOpen, setIsProfileModalOpen ] = useState<boolean>(false);

    const modalRefLng = useRef<any>(null);
    const modalRefProfile = useRef<any>(null);
    const prevSupportUnreadCountRef = useRef<number | null>(null);

    useEffect(() => {
        if (isLngModalOpen) {
            document.addEventListener('mousedown', _handleOutsideLngClick);
        } else {
            document.removeEventListener('mousedown', _handleOutsideLngClick);
        }

        return () => {
            document.removeEventListener('mousedown', _handleOutsideLngClick);
        };
    }, [ isLngModalOpen ]);

    useEffect(() => {
        if (isProfileModalOpen) {
            document.addEventListener('mousedown', _handleOutsideProfileClick);
        } else {
            document.removeEventListener('mousedown', _handleOutsideProfileClick);
        }

        return () => {
            document.removeEventListener('mousedown', _handleOutsideProfileClick);
        };
    }, [ isProfileModalOpen ]);

    useEffect(() => {
        const _messageListener = (event: MessageEvent<any>) => {
            const { data, origin } = event;
            const { type, text } = data;

            console.log('[ PLAYHUB ] получил сообщение:', { data, origin, type, text });

            if (!data || typeof data !== 'object') {
                console.error('[ PLAYHUB ] Сообщение не объект:', { data });
                return;
            }

            if (!IFRAME_ORIGINS.includes(origin)) {
                console.error('[ PLAYHUB ] Сообщение отклонено из-за origin:', { origin });
                return;
            }

            if (type === 'IFRAME_TO_PLAYHUB_GET_USER_AUTH') {
                _getUserAuth();
                return;
            }

            if (type === 'IFRAME_TO_PLAYHUB_ON_LOGIN') {
                _onLogin(text);
                return;
            }

            if (type === 'IFRAME_TO_PLAYHUB_ON_LOGOUT') {
                _onLogOut();
                return;
            }

            if (type === 'IFRAME_TO_PLAYHUB_ON_BANK') {
                _onBankClick(text);
                return;
            }

            if (type === 'IFRAME_TO_PLAYHUB_CHANGE_LNG') {
                changeLanguage(text).then(r => r);
                return;
            }

            if (type === 'IFRAME_TO_PLAYHUB_GET_UNREAD_SUPPORT_MSG') {
                _getSupportUnreadMessageCount();
                return;
            }

            if (type === 'IFRAME_TO_PLAYHUB_ON_SUPPORT') {
                _onSupportClick();
                return;
            }

            console.error('[ PLAYHUB ] Неизвестный тип:', { type });
            return;
        }

        window.addEventListener('message', _messageListener);

        return () => window.removeEventListener('message', _messageListener);
    }, []);

    useEffect(() => {
        if (prevSupportUnreadCountRef.current !== null && prevSupportUnreadCountRef.current !== supportUnreadMessageCount) {
            _sendMessageToIframe('PLAYHUB_TO_IFRAME_UNREAD_SUPPORT_MSG', JSON.stringify({ count: supportUnreadMessageCount }));
        }

        prevSupportUnreadCountRef.current = supportUnreadMessageCount;
    }, [ supportUnreadMessageCount ]);

    const _getSupportUnreadMessageCount = () => {
        _sendMessageToIframe('PLAYHUB_TO_IFRAME_UNREAD_SUPPORT_MSG', JSON.stringify({ count: prevSupportUnreadCountRef.current }));
    }

    const _sendMessageToIframe = (type: PLAYHUB_TO_IFRAME, text: string = '') => {
        const iframe = document.getElementById(GAME_IFRAME_ID) as HTMLIFrameElement;

        if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage({ type, text }, getIframeSrcOrigin());
        }
    }

    const _getUserAuth = () => {
        const webAuthData: IWebAuthData | null = getUserAuthData();

        const text = {
            userId: webAuthData?.userId || null,
            nickName: webAuthData?.nickName || null,
            currentAuthType: webAuthData?.currentAuthType || 'unauthorized',
            jwt: getToken() || null,
            session: getSession() || null,
        }

        _sendMessageToIframe('PLAYHUB_TO_IFRAME_USER_AUTH', JSON.stringify(text));
    }

    const _onBankClick = (text: string) => {
        if (!text) {
            toast.error('Ошибка открытия платежа!');
            return;
        }

        const url = new URL(text);
        const params = new URLSearchParams(url.search);
        const paramsObject: any = Object.fromEntries(params.entries());

        setModalContent({ component: <Payments/>, values: paramsObject });
    }

    const _onLogin = (text: string = ''): void => {
        const createSessionData: ISessionCreateData = {
            iid: generateRandomString(),
            uid: generateRandomNumber(),
            platformId: generateRandomString(),
            source: WEB_SOURCE,
            locale: language,
            project: getProject(),
        }

        if (text) {
            const sessionCreateData: SessionCreateDataWithIframe = JSON.parse(text);

            if (sessionCreateData?.iid) {
                createSessionData['iid'] = sessionCreateData?.iid;
            }

            if (sessionCreateData?.platformId) {
                createSessionData['platformId'] = sessionCreateData?.platformId;
            }

            if (sessionCreateData?.uid) {
                createSessionData['uid'] = sessionCreateData?.uid;
            }
        }

        _sessionGet(createSessionData);
    }

    const _sessionGet = (createSessionData: ISessionCreateData) => {
        toggleLoader(true);

        sessionGet(createSessionData).then(res => {
            const loginUrl: string = res?.data?.loginUrl;

            if (loginUrl) {
                if (settings?.projects?.authType === 'postMessage') {
                    const url = new URL(loginUrl);
                    const params = new URLSearchParams(url.search);
                    const paramsObject: any = Object.fromEntries(params.entries());

                    setSession(res?.data?.session);

                    setModalContent({ component: <AuthMain/>, values: paramsObject });
                    return;
                }

                window.location.href = loginUrl;
                return;
            }

            toast.error('GET_INVALID_SESSION_LOGIN_URL_ERROR');
        }).catch(err => {
            console.error('SESSION_GET_ERROR', { err });
            toast.error('SESSION_GET_ERROR');
        }).finally(() => {
            toggleLoader(false);
        });
    }

    const _changeLng = (newLang: string) => {
        if (newLang !== language) {
            changeLanguage(newLang).then(r => r);

            const iframe = document.getElementById(GAME_IFRAME_ID) as HTMLIFrameElement;

            if (iframe && iframe.contentWindow) {
                _sendMessageToIframe('PLAYHUB_TO_IFRAME_CHANGE_LNG', newLang);
            }
        }
    }

    const _handleOutsideLngClick = (e: any) => {
        if (modalRefLng?.current && !modalRefLng?.current?.contains(e.target)) {
            setIsLngModalOpen(false);
        }
    };

    const _handleOutsideProfileClick = (e: any) => {
        if (modalRefProfile?.current && !modalRefProfile?.current?.contains(e.target)) {
            setIsProfileModalOpen(false);
        }
    };

    const _onLogOut = () => {
        setUserAuth(null, '');
        setIsProfileModalOpen(false);
        removeSession();
        setTempUserId();
        getSupportUnreadMessageCountPublic();

        const iframe = document.getElementById(GAME_IFRAME_ID) as HTMLIFrameElement;

        if (iframe && iframe.contentWindow) {
            _sendMessageToIframe('PLAYHUB_TO_IFRAME_USER_LOGOUT');
        }
    }

    const _onProfileClick = () => {
        if (userAuthData?.nickName) {
            setIsProfileModalOpen((prev) => !prev);
        }
    }

    const _onSupportClick = () => {
        if (settings?.projects?.authType === 'postMessage') {
            setIsProfileModalOpen(false);

            setModalContent({ component: <SupportTickets/>, className: 'support-tickets-modal' });
            return;
        }

        navigate('/support/tickets');
    }

    const _renderOpenProfileModal = () => {
        const userId: string = userAuthData?.userId.toString() || '';

        return (
            <div className="games-header-user-modal" ref={modalRefProfile}>
                <ul className="games-header-user-modal-list">
                    {_renderUserAuthType()}
                    <li key={2} className="games-header-user-modal-list-item">
                        <div className="games-header-user-modal-list-item-icon">
                            <img className="games-header-logo-img" alt="" src={PROFILE_ID_LOGO}/>
                        </div>
                        <div className="games-header-user-modal-list-item-title">
                            ID #{userId}
                        </div>
                    </li>
                    <li key={3} className="games-header-user-modal-list-item">
                        <div className="games-header-user-modal-list-item-icon">
                            <img className="games-header-logo-img" alt="" src={PROFILE_SETTINGS_LOGO}/>
                        </div>
                        <div className="games-header-user-modal-list-item-title">
                            {t('profile.settings')}
                        </div>
                    </li>
                    <li
                        key={4}
                        className="games-header-user-modal-list-item active-item"
                        onClick={() => {
                            if (settings?.projects?.authType === 'postMessage') {
                                setIsProfileModalOpen(false);

                                setModalContent({component: <Transactions/>});
                                return;
                            }

                            navigate('/transactions');
                        }}
                    >
                        <div className="games-header-user-modal-list-item-icon">
                            <img className="games-header-logo-img" alt=""
                                 src={PROFILE_TRANSACTIONS_LOGO}/>
                        </div>
                        <div className="games-header-user-modal-list-item-title">
                            {t('profile.transactions')}
                        </div>
                    </li>
                    <li
                        key={4}
                        className="games-header-user-modal-list-item active-item"
                        onClick={_onSupportClick}
                    >
                        <div className="games-header-user-modal-list-item-icon">
                            <img className="games-header-logo-img" alt="" src={PROFILE_SUPPORT_LOGO}/>
                        </div>
                        <div className="games-header-user-modal-list-item-title">
                            {t('profile.support')}
                            {
                                supportUnreadMessageCount > 0 ?
                                    <div className="games-header-user-modal-list-item-notif">!</div>
                                : null
                            }
                        </div>
                    </li>
                    <li
                        key={5}
                        className="games-header-user-modal-list-item active-item"
                        onClick={_onLogOut}
                    >
                        <div className="games-header-user-modal-list-item-icon">
                            <img className="games-header-logo-img" alt="" src={PROFILE_LOGOUT_LOGO}/>
                        </div>
                        <div className="games-header-user-modal-list-item-title">
                            {t('login.logout')}
                        </div>
                    </li>
                </ul>
            </div>
        );
    }

    const _renderProfile = () => {
        const authExist: boolean = Boolean(userAuthData?.nickName);
        const menuUserClassName: string = `games-header-menu-user text-overflow ${projectData ? 'games-header-menu-user-lng-value-single' : ''}`;

        if (!authExist) {
            return (
                <div className="games-header-user-non-auth" onClick={_onSupportClick}>
                    <div className="games-header-user-profile-non-auth">
                        <img
                            className="games-header-menu-user-img-non-auth"
                            alt=""
                            src={PROFILE_SUPPORT_LOGO}
                        />
                        {
                            supportUnreadMessageCount > 0 ?
                                <div className="games-header-user-non-auth-notif">!</div>
                            : null
                        }
                    </div>
                    <div className={menuUserClassName}>
                        {t('profile.support')}
                    </div>
                </div>
            );
        }

        return (
            <div className="games-header-user">
                <div className={menuUserClassName}>
                    { userAuthData?.nickName }
                </div>
                <div className="games-header-user-profile">
                    <img
                        className="games-header-menu-user-img"
                        alt=""
                        src={PROFILE_LOGO}
                        onClick={_onProfileClick}
                    />
                    { isProfileModalOpen && _renderOpenProfileModal()}
                    {
                        supportUnreadMessageCount > 0 ?
                            <div className="games-header-user-profile-notif">!</div>
                        : null
                    }
                </div>
            </div>
        );
    };

    const _renderLngMenu = () => {
        return (
            <div className="games-header-menu-user-lng" onClick={() => setIsLngModalOpen((prev) => !prev)}>
                <div
                    className={`games-header-menu-user-lng-value ${projectData ? 'games-header-menu-user-lng-value-single' : ''}`}>
                    {language.toUpperCase()}
                </div>
                <div className="games-header-menu-user-lng-select"/>
                {
                    isLngModalOpen && (
                        <div className="games-header-language-modal" ref={modalRefLng}>
                            <ul className="games-header-language-list">
                                {
                                    LANGUAGES.map((lang: ILanguages, index: number) => {
                                        const {id, title} = lang;
                                        const key: string = `${id}_${index}`;
                                        const style: any = {};

                                        if (language === id) {
                                            style['background'] = '#D9D9D9';
                                            style['color'] = '#181914';
                                        }

                                        return (
                                            <li
                                                key={key}
                                                className="games-header-language-item"
                                                onClick={() => _changeLng(id)}
                                                style={style}
                                            >
                                                {title}
                                            </li>
                                        );
                                    })
                                }
                            </ul>
                        </div>
                    )}
            </div>
        );
    };

    const _getHeaderGameLogo = () => {
        if (projectData?.gameLogoFile) {
            return `${STATIC_FILES_PATH}${projectData.gameLogoFile}`;
        } else {
            return HEADER_LOGO;
        }
    };

    return (
        <div className="games-header">
            <div className="games-header-logo">
                <div
                    className="games-header-logo-block"
                    onClick={() => isGame ? navigate('/games') : null}
                    style={{
                        cursor: isGame ? 'pointer' : 'auto',
                        transform: isHovered ? 'scale(1.03)' : 'scale(1)',
                    }}
                    onMouseEnter={() => isGame ? setIsHovered(true) : null}
                    onMouseLeave={() => isGame ? setIsHovered(false) : null}
                >
                    <img className="games-header-logo-img" alt="" src={_getHeaderGameLogo()}/>
                </div>
            </div>
            <div className={`games-header-menu ${projectData ? 'games-header-menu-single' : ''}`}>
                {
                    !userAuthData ?
                        <div className="games-header-menu-block">
                            { _renderProfile() }
                            <hr className="games-header-menu-user-divide"/>
                            { _renderLngMenu() }
                            <hr className="games-header-menu-user-divide"/>
                            <div className="games-header-login-btn" onClick={() => _onLogin()}>
                                {t('games.header.buttonLogin')}
                            </div>
                        </div>
                    :
                        <div className="games-header-menu-block">
                            { _renderLngMenu() }
                            <hr className="games-header-menu-user-divide"/>
                            { _renderProfile() }
                        </div>
                }
            </div>
        </div>
    );
}

const _renderUserAuthType = () => {
    let type: AUTH_TYPE|string = '';
    let icon: any = '';

    const webAuthData: IWebAuthData | null = getUserAuthData();

    if (webAuthData?.currentAuthType) {
        const authType: AUTH_TYPE = webAuthData.currentAuthType;
        type = authType.charAt(0).toUpperCase() + authType.slice(1);

        if (authType === 'google') {
            icon = <GoogleSvg width="24" height="24"/>;
        } else if (authType === 'apple') {
            icon = <AppleSvg width="24" height="24"/>;
        } else if (authType === 'email') {
            icon = <MailSvg width="24" height="24"/>;
        } else if (authType === 'vk') {
            icon = <VkSvg width="24" height="24"/>;
        }  else if (authType === 'yandex') {
            icon = <YandexSvg width="24" height="24"/>;
        }  else if (authType === 'telegram') {
            icon = <TelegramSvg width="24" height="24"/>;
        }  else if (authType === 'facebook') {
            icon = <FacebookSvg width="24" height="24"/>;
        }
    }

    return (
        <li key={1} className="games-header-user-modal-list-item">
            <div className="games-header-user-modal-list-item-icon">
                {icon}
            </div>
            <div className="games-header-user-modal-list-item-title" style={{ color: '#181914' }}>
                {type}
            </div>
        </li>
    );
}